import { useApiRequest } from '@/lib/http-utils'
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query'
import { taskManagerApiPaths } from './_paths'
import { turnStatusKeyFactory } from './use-turn-status-query'

type ResumeJourneyApiResponse = {
  content: string
  live_session_view: string | null
  recording_url: string | null
  message_type: string
  journey_view_reference: string | null
}
export function useResumeJourneyMutation(
  { workflowId, turnId }: { workflowId: string; turnId: string },
  options: Omit<
    UseMutationOptions<ResumeJourneyApiResponse, Error, string>,
    'mutationFn' | 'mutationKey'
  > = {},
) {
  const queryClient = useQueryClient()
  const { apiRequest, domainId } = useApiRequest()
  const turnQueryKey = turnStatusKeyFactory({ workflowId, turnId, domainId })
  return useMutation({
    mutationKey: ['resume-journey'],
    mutationFn: async (journeyViewReference: string) => {
      const response = await apiRequest({
        path: taskManagerApiPaths.resumeJourney({ journeyViewReference }),
        options: {
          method: 'POST',
          body: JSON.stringify({ workflow_id: workflowId }),
        },
      })

      if (!response.ok) throw new Error('FAILED_TO_RESUME_JOURNEY')

      const data = (await response.json()) as ResumeJourneyApiResponse

      return data
    },
    onSettled: async (data, error, variables, result, context) => {
      await queryClient.cancelQueries({ queryKey: turnQueryKey })
      await queryClient.invalidateQueries({ queryKey: turnQueryKey })
      if (options.onSettled) {
        await options.onSettled(data, error, variables, result, context)
      }
    },
    ...options,
  })
}
